import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  // Refresh-токен живёт в httpOnly-cookie — cookie должны ходить с запросами.
  withCredentials: true,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const auth = useAuthStore();
  if (auth.accessToken) {
    config.headers.Authorization = `Bearer ${auth.accessToken}`;
  }
  return config;
});

/** Один refresh на все параллельные 401 — иначе каждая вкладка/запрос ротировали бы токен наперегонки. */
let refreshing: Promise<string | null> | null = null;
const AUTH_URLS = ['/auth/login', '/auth/refresh', '/auth/logout'];

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const auth = useAuthStore();

    // Ворота документов: сервер не пустит дальше, пока новые версии не приняты — поднимаем блокирующий экран.
    if (error.response?.status === 403 && errorCode(error) === 'legal_acceptance_required') {
      void auth.loadPendingLegal();
    }

    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      auth.accessToken &&
      !AUTH_URLS.some((u) => original.url?.includes(u))
    ) {
      original._retry = true;
      refreshing ??= auth.refresh().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }
      // Сессия кончилась: App.vue отправит на страницу входа.
      window.dispatchEvent(new CustomEvent('auth:expired'));
    }

    return Promise.reject(error);
  },
);

/** Машинный код ошибки из ProblemDetails (`code`), либо `network`, если ответа не было. */
export function errorCode(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) return 'network';
    const code = (error.response.data as { code?: unknown } | undefined)?.code;
    if (typeof code === 'string') return code;
  }
  return 'internal_error';
}

/** Дополнительное поле ProblemDetails, например `retryAfterSeconds`. */
export function errorExtension<T>(error: unknown, key: string): T | undefined {
  if (axios.isAxiosError(error) && error.response) {
    return (error.response.data as Record<string, unknown> | undefined)?.[key] as T | undefined;
  }
  return undefined;
}
